/**
 * useNotificationsFeed — canonical, cross-source feed powering the
 * top-bar bell dropdown.
 *
 * Sources
 *   • Advisor drop-offs (`/api/inbox/requests`) — advisors delivering
 *     documents / quotes to this device.
 *   • Passport review — missing critical documents + overdue reviews.
 *   • Financial Profile — one nag until it's complete.
 *
 * "Read" state
 *   Persists a Set of {id}:{version} keys in
 *   `altara.wallet.notif_read` so items don't stay red after the user
 *   has seen them.  Refreshes every 45s while the wallet is open.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { loadWallet } from "@/lib/wallet-storage";
import { usePassportReviewItems, formatAge } from "@/lib/wallet-passport-review";
import { DECISION_STATUS } from "@/lib/wallet-decisions";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const READ_KEY = "altara.wallet.notif_read";
const POLL_MS = 45_000;

function loadRead() {
  try { return new Set(JSON.parse(localStorage.getItem(READ_KEY) || "[]")); }
  catch { return new Set(); }
}
function saveRead(set) {
  try { localStorage.setItem(READ_KEY, JSON.stringify([...set])); } catch { /* ignore */ }
}

export function useNotificationsFeed() {
  const wallet = loadWallet() || {};
  const deviceId = wallet.device_id;
  const [inbox, setInbox] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [profileComplete, setProfileComplete] = useState(null);
  const [readSet, setReadSet] = useState(() => loadRead());
  const passport = usePassportReviewItems();

  // Inbox polling
  useEffect(() => {
    if (!deviceId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/inbox/requests`, { params: { device_id: deviceId } });
        if (!cancelled) setInbox(Array.isArray(data) ? data : []);
      } catch { /* silent */ }
    };
    tick();
    const iv = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(iv); };
  }, [deviceId]);

  // Active decisions polling
  useEffect(() => {
    if (!deviceId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/wallet/decisions`, { params: { device_id: deviceId } });
        if (!cancelled) setDecisions(Array.isArray(data?.active) ? data.active : []);
      } catch { /* silent */ }
    };
    tick();
    const iv = setInterval(tick, POLL_MS);
    const onRefresh = () => tick();
    window.addEventListener("altara.decisions.refresh", onRefresh);
    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener("altara.decisions.refresh", onRefresh);
    };
  }, [deviceId]);

  // Financial profile status
  useEffect(() => {
    if (!deviceId) { setProfileComplete(false); return; }
    axios.get(`${API}/wallet/financial-profile`, { params: { device_id: deviceId } })
      .then(r => setProfileComplete(Boolean(r.data?.profile?.updated_at)))
      .catch(() => setProfileComplete(false));
  }, [deviceId]);

  const items = useMemo(() => {
    const out = [];
    // Decisions waiting on the consumer — highest priority
    for (const d of decisions) {
      if (d.status === DECISION_STATUS.TRUST) {
        out.push({
          id: `decision:${d.decision_id}:trust`,
          kind: "decision",
          title: `${d.institution_name} sent you an investment proposal`,
          detail: `${d.proposal_name} · confirm who sent it first.`,
          when: d.sent_at,
          to: `/wallet/decisions/${d.decision_id}`,
        });
      } else {
        out.push({
          id: `decision:${d.decision_id}:${d.status}`,
          kind: "decision",
          title: `${d.proposal_name} · continue`,
          detail: `Next step: ${d.stage.toLowerCase()}.`,
          when: d.updated_at || d.sent_at,
          to: `/wallet/decisions/${d.decision_id}`,
        });
      }
    }
    // Advisor drop-offs — newest first
    for (const r of inbox) {
      if (r.status !== "received" || (r.upload_count || 0) === 0) continue;
      const id = `inbox:${r.id}:${r.upload_count}`;
      out.push({
        id,
        kind: "inbox",
        title: `${r.depositor_name || r.institution_hint || "Your advisor"} delivered your ${(r.product_type || "document").replace(/_/g, " ")}`,
        detail: "NAVI is reading it now — tap to open.",
        when: r.updated_at || r.received_at || r.created_at,
        to: "/wallet/documents",
      });
    }
    // Passport review
    for (const m of (passport.missing || [])) {
      out.push({
        id: `passport-missing:${m.category}`,
        kind: "passport",
        title: `${m.label} · nothing on file`,
        detail: "Upload to complete your Trust Wallet.",
        when: null,
        to: "/wallet/documents",
      });
    }
    for (const o of (passport.overdue || [])) {
      out.push({
        id: `passport-overdue:${o.category}`,
        kind: "passport",
        title: `${o.label} · review recommended`,
        detail: `Last updated ${formatAge(o.age_months)} ago.`,
        when: null,
        to: "/wallet/documents",
      });
    }
    // Financial profile
    if (profileComplete === false) {
      out.push({
        id: "profile:incomplete",
        kind: "profile",
        title: "Your Financial Profile is incomplete",
        detail: "Five quick questions — takes under two minutes.",
        when: null,
        to: "/wallet/profile",
      });
    }
    return out.map(it => ({ ...it, unread: !readSet.has(it.id) }));
  }, [inbox, decisions, passport, profileComplete, readSet]);

  const unreadCount = items.filter(i => i.unread).length;

  const markRead = (id) => {
    setReadSet(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev); next.add(id); saveRead(next); return next;
    });
  };
  const markAllRead = () => {
    setReadSet(prev => {
      const next = new Set(prev);
      for (const it of items) next.add(it.id);
      saveRead(next);
      return next;
    });
  };

  return { items, unreadCount, markRead, markAllRead };
}
